// src/modules/smelter/pages/DispatchList.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchDispatches, deleteDispatch } from '../api';
import {
  Table, Button, Typography, Spin, Tag, Row, Col, Select, message, Space, Popconfirm,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { usePermissions } from '../../../hooks/usePermissions';

const { Title } = Typography;
const STATUS_COLORS = { DISPATCHED: 'orange', RECEIPT_CONFIRMED: 'blue', PAYMENT_RECEIVED: 'green' };
const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

export default function DispatchList() {
  const navigate = useNavigate();
  const { canViewCreateModule, canViewField, can, role } = usePermissions();
  const isAdmin = role === 'admin';

  const canViewSmelterName = canViewField('smelter', 'name');
  // Profit is money data, not operational data: a dispatch clerk can be given
  // the dispatch screens without being shown the margin. Gated on the
  // dedicated `inventoryProfit` module (admins bypass as everywhere).
  const canViewProfit = can('inventoryProfit', 'view');
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [deletingId, setDeletingId] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => { load(); }, [pagination.current, pagination.pageSize, statusFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchDispatches({ page: pagination.current, limit: pagination.pageSize, status: statusFilter });
      setDispatches(res.data || []);
      setPagination((p) => ({ ...p, total: res.meta?.total || 0 }));
    } catch { messageApi.error('Failed to load dispatches'); }
    finally { setLoading(false); }
  };

  const columns = [
    { title: 'Dispatch ID', dataIndex: 'dispatchId', key: 'dispatchId', width: 140,
      render: (v, r) => <Button type="link" onClick={() => navigate(`/smelters/dispatches/${r._id}`)}>{v}</Button> },
    canViewSmelterName && { title: 'Smelter', key: 'smelter', width: 180,
      render: (_, r) => r.smelterId?.name || r.smelterName || '—' },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 190,
      render: (v, r) => (
        <Space direction="vertical" size={2}>
          <Tag color={STATUS_COLORS[v]} style={{ margin: 0 }}>{v?.replace(/_/g, ' ')}</Tag>
          {/* A billed dispatch the smelter has only part-paid stays RECEIPT_CONFIRMED —
              this says how far along it is without inventing a status. */}
          {v === 'RECEIPT_CONFIRMED' && r.settledTillDate > 0 && (
            <Tag color="gold" style={{ margin: 0, fontSize: 11 }}>
              part paid {fmt(r.settledTillDate)} / {fmt(r.totalAmountFromSmelter)}
            </Tag>
          )}
        </Space>
      ) },
    { title: 'Net Wt (g)', dataIndex: 'dispatchedNetWeight', key: 'dispatchedNetWeight', width: 110,
      render: (v) => v?.toFixed(3) },
    { title: 'Refined Wt (g)', dataIndex: 'refinedWeight', key: 'refinedWeight', width: 120,
      render: (v) => v ? v.toFixed(3) : '—' },
    { title: 'Total From Smelter', dataIndex: 'totalAmountFromSmelter', key: 'totalAmountFromSmelter', width: 160,
      render: (v) => v ? fmt(v) : '—' },
    canViewProfit && { title: 'Gross Profit', key: 'profit', width: 130,
      render: (_, r) => r.profitBreakdown?.grossProfit ? (
        <span style={{ color: r.profitBreakdown.grossProfit > 0 ? '#389e0d' : '#cf1322' }}>
          {fmt(r.profitBreakdown.grossProfit)}
        </span>
      ) : '—' },
    { title: 'Dispatch Date', dataIndex: 'dispatchDate', key: 'dispatchDate', width: 120,
      render: (v) => v ? new Date(v).toLocaleDateString('en-IN') : '—' },
    { title: 'Actions', key: 'actions', width: 150, fixed: 'right',
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => navigate(`/smelters/dispatches/${r._id}`)}>View</Button>
          {can('smelter', 'delete') && (isAdmin || r.status !== 'PAYMENT_RECEIVED') && (
            <Popconfirm
              title="Delete this dispatch?"
              description={isAdmin && r.status === 'PAYMENT_RECEIVED'
                ? 'Admin cascade: profit records will also be deleted and batch reverted.'
                : 'Batch will return to HOLDING.'}
              okText="Delete"
              okButtonProps={{ danger: true, loading: deletingId === r._id }}
              onConfirm={() => handleDelete(r._id)}
            >
              <Button
                danger
                size="small"
                loading={deletingId === r._id}
              >
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ) },
  ].filter(Boolean);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deleteDispatch(id);
      messageApi.success('Dispatch deleted');
      await load();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to delete dispatch');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      {contextHolder}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button onClick={() => navigate('/smelters')}>Smelter Directory</Button>
            <Title level={4} style={{ margin: 0 }}>Smelter Dispatches</Title>
          </Space>
        </Col>
        <Col>
          {canViewCreateModule('smelter') && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/smelters/dispatches/create')}>
              New Dispatch
            </Button>
          )}
        </Col>
      </Row>

      <Row style={{ marginBottom: 12 }}>
        <Col>
          <Select
            placeholder="All Statuses" allowClear style={{ width: 200 }}
            value={statusFilter || undefined}
            onChange={(v) => { setPagination((p) => ({ ...p, current: 1 })); setStatusFilter(v || ''); }}
          >
            {['DISPATCHED', 'RECEIPT_CONFIRMED', 'PAYMENT_RECEIVED'].map((s) => (
              <Select.Option key={s} value={s}>{s.replace(/_/g, ' ')}</Select.Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Spin spinning={loading}>
        <Table
          dataSource={dispatches} rowKey="_id" columns={columns} scroll={{ x: 900 }} size="small"
          pagination={{
            current: pagination.current, pageSize: pagination.pageSize, total: pagination.total,
            showSizeChanger: true,
            onChange: (page, size) => setPagination({ current: page, pageSize: size, total: pagination.total }),
          }}
        />
      </Spin>
    </div>
  );
}
